import {
  Box,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Button,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { s3baseUri } from "../../config/config";
import { useAdminContext } from "../../hooks/UseAdminContext";
import logoImage from "../assets/images/Logo/transparency background/Little Haiti Global-08.png";
import html2pdf from "html2pdf.js";
import { useRef, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";

const getFileTypeIcon = (fileUrl) => {
  const extension = fileUrl?.path?.split(".").pop().toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return <PictureAsPdfIcon style={{ color: "#15978a" }} />;
  }
  if (["doc", "docx"].includes(extension)) {
    return <DescriptionIcon style={{ color: "#15978a" }} />;
  }
  if (["xls", "xlsx"].includes(extension)) {
    return <InsertDriveFileIcon style={{ color: "#15978a" }} />;
  }

  return <InsertDriveFileIcon style={{ color: "#15978a" }} />;
};

const ReviewGettingStartedSection = () => {
  const { sectionList, userInfo, addonCaseNumber } = useAdminContext();
  const displayCaseNumber = addonCaseNumber || userInfo?.case_number;

  const pdfRef = useRef();
  const [loading, setLoading] = useState(false);

  const handleDownloadPDF = () => {
    setLoading(true);
    const element = pdfRef.current;

    element.querySelectorAll("div, p, span").forEach((el) => {
      el.style.fontSize = "16px";
    });

    const opt = {
      margin: 0.5,
      filename: `${displayCaseNumber || "form-data"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("PDF generation failed:", error);
        setLoading(false);
      });
  };

  const renderAnswer = (question) => {
    if (question?.question_type === "file") {
      if (Array.isArray(question?.answer) && question?.answer.length > 0) {
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            {question?.answer.map((fileUrl, idx) => {
              const fileType = getFileTypeIcon(fileUrl);
              return (
                <div className="mt-2" key={idx} style={{ width: "200px" }}>
                  {fileType === "image" ? (
                    <a
                      href={s3baseUri + fileUrl?.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pointer"
                    >
                      <img
                        src={s3baseUri + fileUrl?.path}
                        alt={`Uploaded file ${idx + 1}`}
                        style={{
                          maxWidth: "100%",
                          borderRadius: "4px",
                          display: "block",
                        }}
                      />
                    </a>
                  ) : (
                    <div
                      className="mt-2"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {fileType}
                      <a
                        href={s3baseUri + fileUrl?.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          textDecoration: "underline",
                        }}
                      >
                        View Document {idx + 1}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      } else {
        return "No file uploaded";
      }
    }

    if (question?.question_type === "boxwithcheck") {
      return question?.answer === true ? "Yes" : "No";
    }

    if (question?.question_type === "electronic_sign") {
      return question?.answer ? (
        <a
          href={s3baseUri + question.answer}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={s3baseUri + question.answer}
            alt="Electronic Signature"
            style={{ maxWidth: "200px", borderRadius: "4px", display: "block" }}
          />
        </a>
      ) : (
        "No signature provided"
      );
    }

    if (question?.options && Array.isArray(question?.options)) {
      const selected = question?.options.find(
        (opt) => opt.value === question?.answer
      );
      if (selected) {
        return selected.label;
      }
      if (
        question?.created_for === "optional_wellness_support_services" &&
        !question?.answer
      ) {
        return "N/A";
      }
      return "N/A";
    }

    return typeof question?.answer === "string" &&
      question?.answer?.startsWith("file") ? (
      <div
        className="mt-2"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {getFileTypeIcon({ path: question?.answer })}
        <a
          href={s3baseUri + question?.answer}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#1976d2",
            textDecoration: "underline",
          }}
        >
          View Document
        </a>
      </div>
    ) : (
      <>
        <span>{question?.answer?.toString() || "N/A"}</span>
      </>
    );
  };

  const checkcountry =
    sectionList?.find((q) => q.value === "visa_type_and_application_details")
      ?.questions?.find(q => q.created_for === "country_of_residence")?.answer === "haiti";

  const newData = sectionList.slice(0, -3).filter(s => !s.hide_section).map((section) => {
    if (section?.value === "required_documentation") {
      return {
        ...section,
        questions: section?.questions.filter((q) => {
          if (q.created_for === "dcpj_data_page") {
            return checkcountry;
          }
          return true;
        }),
      };
    }
    // Hide housing options if housing_need is not 'yes'
    if (section?.value === "housing_option") {
      const housingNeed = section?.questions.find(
        (q) => q.created_for === "housing_need"
      )?.answer;

      if (housingNeed !== "yes") {
        return {
          ...section,
          questions: section.questions.filter(
            (q) => q.created_for === "housing_need"
          ),
        };
      }
    }

    return section;
  });

  return (
    <>
      <div style={{ textAlign: "end", position: "relative" }}>
        <Tooltip title="Download your Review form">
          <span>
            <Button
              className="mui-button mb-3"
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              startIcon={<DownloadIcon />}
              disabled={loading}
              sx={{
                minWidth: 160,
                whiteSpace: "nowrap",
              }}
            >
              {loading ? "Downloading..." : "Download PDF"}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div className="pdf-content">
        <div ref={pdfRef}>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <img src={logoImage} alt="Logo" width={120} height={120} />
          </div>
          {newData.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              style={{
                marginBottom: "20px",
              }}
            >
              <h2>{section.title}</h2>
              {section?.value === "getting_started" && (
                <h4>Eligibility Certification Statement</h4>
              )}
              {section?.intro_text && <p>{section?.intro_text}</p>}
              <hr />

              {section.questions?.map((q, qIndex) => {
                if (q.question_type === "description" || q.question_type === "heading") {
                  return (
                    <div
                      key={qIndex}
                      style={{
                        marginBottom: "15px",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: q.question_statement }} />
                    </div>
                  );
                }
                const ans = q?.answer;
                return (
                  <div
                    key={qIndex}
                    style={{
                      marginBottom: "15px",
                      pageBreakInside: "avoid",
                      breakInside: "avoid",
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: q.question_statement }} />
                    {!q.is_not_show_ans && (
                      <div className="mt-1">
                        <strong>Answer:</strong>{" "}
                        {q.question_type === "electronic_sign" ? (
                          q.answer ? (
                            <img
                              src={s3baseUri + q.answer}
                              alt="Electronic Signature"
                              style={{ maxWidth: "150px", display: "block" }}
                            />
                          ) : (
                            "No signature provided"
                          )
                        ) : Array.isArray(ans) ? (
                          ans.map((file, i) => {
                            const filePath =
                              typeof file === "string" ? file : file?.path;

                            return typeof filePath === "string" &&
                              filePath?.startsWith("file") ? (
                              <span key={i}>
                                <a
                                  href={`${s3baseUri}${filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {`${s3baseUri}${filePath}`}
                                </a>
                              </span>
                            ) : (
                              <span key={i}>{filePath || "N/A"}</span>
                            );
                          })
                        ) : typeof ans === "string" && ans?.startsWith("file") ? (
                          <a
                            href={`${s3baseUri}${ans}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {`${s3baseUri}${ans}`}
                          </a>
                        ) : q?.options && Array.isArray(q.options) ? (
                          q.options.find((opt) => opt.value === ans)?.label ||
                          ans?.toString() ||
                          "N/A"
                        ) : q?.question_type === "boxwithcheck" ? (
                          ans === true ? (
                            "Yes"
                          ) : (
                            "No"
                          )
                        ) : q?.created_for ===
                          "optional_wellness_support_services" && !ans ? (
                          "N/A"
                        ) : q?.question_type === "file" ? (
                          ans || "No file uploaded"
                        ) : (
                          ans?.toString() || "N/A"
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {section.sub_sections?.map((sub, subIndex) => (
                <div key={subIndex} style={{ marginBottom: "20px" }}>
                  <h3>{sub.title}</h3>
                  {sub.questions?.map((q, qIndex) => {
                    if (q.question_type === "description" || q.question_type === "heading") {
                      return (
                        <div
                          key={qIndex}
                          style={{
                            marginBottom: "15px",
                            pageBreakInside: "avoid",
                            breakInside: "avoid",
                          }}
                        >
                          <div dangerouslySetInnerHTML={{ __html: q.question_statement }} />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={qIndex}
                        style={{
                          marginBottom: "15px",
                          pageBreakInside: "avoid",
                          breakInside: "avoid",
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: q.question_statement }} />
                        {!q.is_not_show_ans && (
                          <div className="mt-1">
                            <strong>Answer:</strong>{" "}
                            {Array.isArray(q.answer)
                              ? q.answer.map((file, i) => (
                                <span key={i}>
                                  <a
                                    href={`${s3baseUri}${file.path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {s3baseUri}
                                    {file.path}
                                  </a>
                                </span>
                              ))
                              : q?.options && Array.isArray(q.options)
                                ? q.options.find((opt) => opt.value === q.answer)
                                  ?.label ||
                                q.answer?.toString() ||
                                "N/A"
                                : q?.question_type === "boxwithcheck"
                                  ? q.answer === true
                                    ? "Yes"
                                    : "No"
                                  : q?.created_for ===
                                    "optional_wellness_support_services" && !q.answer
                                    ? "N/A"
                                    : q?.question_type === "file"
                                      ? q.answer || "No file uploaded"
                                      : q.answer?.toString() || "N/A"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {newData.map((section, index) => (
        <Box key={index} mb={4} mt={2}>
          <Card
            className="pdf-section"
            sx={{
              width: "100%",
              boxShadow: 3,
              borderLeft: "5px solid",
              borderColor: "#15978a",
            }}
          >
            <CardContent>
              <h4 className="mb-2">
                {section.title}
              </h4>
              {section?.value === "getting_started" && (
                <p className="mb-1">
                  Eligibility Certification Statement
                </p>
              )}
              {section?.intro_text && <p>{section?.intro_text}</p>}

              {section.questions?.map((q, idx) => (
                <Box key={idx} mb={3}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: q.question_statement,
                    }}
                  />
                  {q.question_type !== "description" &&
                    q.question_type !== "heading" &&
                    q.question_type !== "statement" &&
                    !q.is_not_show_ans && (
                      <p className="mb-0 mt-1 fw-600">
                        Answer: {renderAnswer(q)}
                      </p>
                    )}
                  {idx < section.questions.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </Box>
              ))}

              {section.sub_sections?.map((sub, subIdx) => (
                <Box key={subIdx} mt={subIdx > 0 ? 4 : 3}>
                  <h5 className="fw-600 mb-2">
                    {sub.title}
                  </h5>
                  {sub.questions.map((q, qIdx) => (
                    <Box key={qIdx} mb={2}>
                      <p style={{ fontWeight: 400 }}>
                        {/<[a-z][\s\S]*>/i.test(q.question_statement) ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: q.question_statement,
                            }}
                          />
                        ) : (
                          q.question_statement
                        )}
                      </p>

                      {q.question_type !== "description" &&
                        q.question_type !== "heading" &&
                        q.question_type !== "statement" &&
                        !q.is_not_show_ans && (
                          <p
                            className="mb-0 mt-1 fw-600"
                          >
                            Answer: {renderAnswer(q)}
                          </p>
                        )}
                      {qIdx < sub.questions.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      ))}
    </>
  );
};

export default ReviewGettingStartedSection;
